import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@/design-system/ui/Icon';
import { Sheet } from '@/design-system/ui/Sheet';
import { SUPPORT } from '@/core/config/content';
import { toPersianDigits } from '@/core/format/numbers';
import logo from '@/assets/images/app_logo.svg';
import baleIcon from '@/assets/images/bale.svg';
import eitaaIcon from '@/assets/images/eitaa.svg';
import businessLicense from '@/assets/images/business-license.jpg';
import virtualBusinessUnion from '@/assets/images/virtual-business-union.png';

const QUICK_LINKS = [
  { label: 'خرید بلیط بین‌شهری', to: '/trips' },
  { label: 'پیگیری مرسوله', to: '/track' },
  { label: 'چرا بلینو؟', to: '/why-blino' },
  { label: 'مجله بلینو', to: '/magazine' },
];

const SUPPORT_LINKS = [
  { label: 'تماس با پشتیبانی', to: '/contact' },
  { label: 'ثبت شکایت', to: '/complaint' },
  { label: 'همکاری با ما', to: '/careers' },
  { label: 'دفاتر و نمایندگی‌ها', to: '/agencies' },
];

const ENAMAD = {
  link: 'https://trustseal.enamad.ir/?id=542686&Code=AmXxjCXUrephPhaN47cDj5RMGAJ5ifVe',
  image: 'https://trustseal.enamad.ir/logo.aspx?id=542686&Code=AmXxjCXUrephPhaN47cDj5RMGAJ5ifVe',
  code: 'AmXxjCXUrephPhaN47cDj5RMGAJ5ifVe',
} as const;

const socialBtn =
  'grid size-10 place-items-center rounded-full bg-white/10 text-white transition duration-200 hover:-translate-y-0.5 hover:bg-turquoise/50 active:scale-90';
const linkCls =
  'inline-block text-sm text-white/65 transition duration-200 hover:-translate-x-0.5 hover:text-mint';

export function Footer({ className }: { className?: string }) {
  const [licenseOpen, setLicenseOpen] = useState(false);

  return (
    <>
      <footer className={className}>
        <div className="border-turquoise/20 from-navy-deep border-t bg-gradient-to-b to-[#06121f] text-white/80">
          <div className="mx-auto max-w-none px-4 pt-12 pb-6 sm:px-8 lg:px-12">
            <div className="grid grid-cols-2 gap-x-6 gap-y-8 sm:grid-cols-[1.4fr_1fr_1fr] sm:gap-10">
              <div className="col-span-2 sm:col-span-1">
                <div className="mb-3 flex items-center gap-2 font-bold text-white">
                  <img src={logo} alt="" className="size-10 rounded-[11px]" />
                  بلینو | سامانه جامع سفر و بار بین‌شهری
                </div>
                <p className="max-w-xs text-sm leading-loose text-white/60">
                  رزرو آنلاین بلیط تاکسی بین‌شهری (عادی و دربستی)، سفر درب‌به‌درب و رهگیری سریع
                  مرسولات با بیمه رایگان و پشتیبانی ۲۴ ساعته.
                </p>
                <div className="mt-5 flex gap-2">
                  <a
                    href={SUPPORT.social.bale}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="بله"
                    className={socialBtn}
                  >
                    <img src={baleIcon} alt="" className="size-4" />
                  </a>
                  <a
                    href={SUPPORT.social.eitaa}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="ایتا"
                    className={socialBtn}
                  >
                    <img src={eitaaIcon} alt="" className="size-4" />
                  </a>
                  <a
                    href={SUPPORT.social.telegram}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="تلگرام"
                    className={socialBtn}
                  >
                    <Icon name="telegram" size={16} />
                  </a>
                  <a href={`tel:${SUPPORT.phone}`} aria-label="تماس تلفنی" className={socialBtn}>
                    <Icon name="phone" size={16} />
                  </a>
                </div>
              </div>

              <nav aria-label="دسترسی سریع">
                <h2 className="mb-3 text-sm font-bold text-white">دسترسی سریع</h2>
                <ul className="flex flex-col gap-2.5">
                  {QUICK_LINKS.map((l) => (
                    <li key={l.to}>
                      <Link to={l.to} className={linkCls}>
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>

              <nav aria-label="پشتیبانی">
                <h2 className="mb-3 text-sm font-bold text-white">پشتیبانی</h2>
                <ul className="flex flex-col gap-2.5">
                  {SUPPORT_LINKS.map((l) => (
                    <li key={l.to}>
                      <Link to={l.to} className={linkCls}>
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>

            <div className="mt-8 flex flex-col gap-2.5 border-t border-white/10 pt-6 text-xs text-white/65 sm:flex-row sm:flex-wrap sm:gap-x-6 sm:gap-y-3">
              <a
                href={`tel:${SUPPORT.phone}`}
                className="inline-flex items-center gap-1.5 transition hover:text-white"
              >
                <Icon name="phone" size={15} className="text-mint" />
                پشتیبانی: {SUPPORT.phoneDisplay}
              </a>
              <a
                href={`mailto:${SUPPORT.email}`}
                className="inline-flex items-center gap-1.5 transition hover:text-white"
                dir="ltr"
              >
                <Icon name="mail" size={15} className="text-mint" />
                {SUPPORT.email}
              </a>
              <span className="inline-flex items-center gap-1.5">
                <Icon name="pin" size={15} className="text-mint" />
                {SUPPORT.city}
              </span>
            </div>

            <div className="mt-5 flex flex-col gap-4 border-t border-white/10 pt-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap items-center justify-center gap-3 sm:justify-start">
                {/* Business license: shows the QR tile, opens the license image in a modal. */}
                <button
                  type="button"
                  onClick={() => setLicenseOpen(true)}
                  aria-haspopup="dialog"
                  aria-label="مشاهده پروانه کسب"
                  className="shadow-card hover:shadow-floating text-navy inline-flex size-24 flex-col items-center justify-center gap-1 rounded-[14px] bg-white p-2 text-center transition duration-200 hover:-translate-y-0.5 active:scale-95"
                >
                  <Icon name="qr" size={28} />
                  <span className="text-[10px] leading-none font-bold">پروانه کسب</span>
                </button>

                {/* Union membership badge: bundled with the app, no external link. */}
                <div className="shadow-card inline-flex size-24 flex-col items-center justify-center rounded-[14px] bg-white p-2">
                  <img
                    src={virtualBusinessUnion}
                    alt="اتحادیه کشوری کسب و کارهای مجازی"
                    loading="lazy"
                    className="h-11 w-auto object-contain"
                  />
                  <span className="text-navy mt-1 w-full text-center text-[9px] leading-[1.35] font-bold">
                    اتحادیه کشوری کسب و کارهای مجازی
                  </span>
                </div>

                {/* eNAMAD trust seal: the seal image is served by enamad.ir and must keep its original link and code. */}
                <a
                  href={ENAMAD.link}
                  target="_blank"
                  rel="noopener"
                  referrerPolicy="origin"
                  aria-label="نماد اعتماد الکترونیکی"
                  className="shadow-card hover:shadow-floating inline-flex size-24 items-center justify-center rounded-[14px] bg-white p-2 transition duration-200 hover:-translate-y-0.5 active:scale-95"
                >
                  <img
                    src={ENAMAD.image}
                    alt="نماد اعتماد الکترونیکی"
                    referrerPolicy="origin"
                    loading="lazy"
                    className="h-16 w-auto cursor-pointer"
                    {...{ code: ENAMAD.code }}
                  />
                </a>
              </div>

              <p className="text-center text-xs text-white/45 sm:text-start">
                ©{' '}
                {toPersianDigits(
                  new Date().toLocaleDateString('fa-IR-u-nu-latn', { year: 'numeric' }),
                )}{' '}
                بلینو — تمامی حقوق محفوظ است
              </p>
            </div>
          </div>
        </div>
      </footer>

      <Sheet open={licenseOpen} onClose={() => setLicenseOpen(false)} title="پروانه کسب بلینو">
        <img
          src={businessLicense}
          alt="پروانه کسب بلینو"
          className="border-line w-full rounded-lg border"
        />
      </Sheet>
    </>
  );
}
